import { Businesses } from '@prisma/client';
import { toast } from 'react-toastify';
import { subMinutes } from 'date-fns';
import { ValidationError } from 'yup';
import { CUSTOMER_WEB_URL } from '../../constants/global';
import { getQRCode } from '../../utils/qrcode';
import { LoadedUser } from '../../interfaces';
import { DEFAULT_QUEUE_SETTINGS } from '../../constants/queue';
import { isUserAdminOrManager } from '../../utils/global/helper';
import { SelectedAppointment } from '../../types/appointments';
import { getArtistOptionsForService } from '../../services/business';
import { DEFAULT_APT_LENGTH } from '../../utils/form/appointmentForm';
import {
  REASON_OPTIONS,
  RemoveFromQueueReason,
  removeFromQueueSchema,
} from '../../types/queue';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import type useRemoveFromQueue from '../../hooks/queue/useRemoveFromQueue';
import type useLoadingModal from '../../hooks/global/useLoadingModal';
import {
  invalidateAppointment,
  invalidateAppointments,
} from '../../services/appointments';

export const getCheckInURL = (
  business: Pick<Businesses, 'urlPath'> | null | undefined,
) =>
  business ? `${CUSTOMER_WEB_URL}/shop/${business.urlPath || ''}/check-in` : '';

export const onDownloadQRCode = async (
  business: Pick<Businesses, 'urlPath'> | null | undefined,
) => {
  try {
    if (!business) return;
    const url = getCheckInURL(business);
    const qrCode = await getQRCode(url);
    const a = document.createElement('a');
    a.href = qrCode;
    a.download = 'walkin-qr-code.png';
    a.click();
  } catch {
    toast.error('Failed to download QR code');
  }
};

export const userHasAccessToQueue = (user: LoadedUser) => {
  const queueSettings = user?.business?.queueSettings ?? DEFAULT_QUEUE_SETTINGS;
  return (
    user?.business?.type === 'STUDIO' &&
    (isUserAdminOrManager(user) || queueSettings.allowArtistsToPull)
  );
};

export const removeFromQueue = async ({
  appointment,
  user,
  asyncForm,
  removeFromQueue: removeFromQueueFn,
  loadingModal,
}: {
  appointment: SelectedAppointment;
  user: LoadedUser;
  asyncForm: ReturnType<typeof useAwaitedForm>;
  removeFromQueue: ReturnType<typeof useRemoveFromQueue>['mutateAsync'];
  loadingModal: {
    open: ReturnType<typeof useLoadingModal>['openLoadingModal'];
    close: ReturnType<typeof useLoadingModal>['closeLoadingModal'];
  };
}) => {
  try {
    const userCanManage = isUserAdminOrManager(user);

    const artistOptions = getArtistOptionsForService({
      user,
      serviceId: appointment.serviceId
        ? appointment.serviceId.toString()
        : undefined,
    });
    const initialEmployeeId =
      artistOptions.length === 1 ? artistOptions[0].value : '';

    const result = await asyncForm({
      form: {
        title: 'Remove from queue',
        submitButtonText: 'Remove',
        initialValues: {
          reason: '' as RemoveFromQueueReason,
          employeeId: initialEmployeeId || '',
          startDateTime: subMinutes(
            new Date(),
            appointment.appointmentLengthInMinutes ||
              parseInt(DEFAULT_APT_LENGTH, 10),
          ),
          endDateTime: new Date(),
        },
        validationSchema: removeFromQueueSchema,
        inputRows: vals => [
          [
            {
              inputType: 'select',
              label: 'Reason',
              placeholder: 'Select reason',
              name: 'reason',
              options: REASON_OPTIONS.map(reason => ({
                label:
                  reason === 'COMPLETED'
                    ? 'Walk-in was completed'
                    : reason === 'CANCELED'
                    ? 'Walk-in was canceled'
                    : 'Other',
                value: reason,
              })),
            },
          ],
          [
            {
              inputType: 'select',
              label: 'Service Provider',
              placeholder: 'Select service provider',
              name: 'employeeId',
              options: artistOptions,
              isVisible: values => values.reason === 'COMPLETED',
              disabled: !userCanManage,
            },
          ],
          [
            {
              inputType: 'date-time',
              name: 'startDateTime',
              label: 'Start Date & Time',
              placeholder: 'Select start date & time',
              isVisible: values => values.reason === 'COMPLETED',
            },
          ],
          [
            {
              inputType: 'date-time',
              name: 'endDateTime',
              label: 'End Date & Time',
              placeholder: 'Select end date & time',
              isVisible: values => values.reason === 'COMPLETED',
              min: vals.startDateTime,
            },
          ],
        ],
      },
    });
    if (!result) return;

    loadingModal.open({
      header: 'Removing from queue...',
      message:
        'Please wait while the appointment is being removed from the queue',
    });

    const updatedAppointment = await removeFromQueueFn({
      appointmentId: appointment.appointmentId,
      payload: result,
    });

    invalidateAppointment(appointment.appointmentId);

    if (updatedAppointment.status === 'SCHEDULED') {
      invalidateAppointments();
    }
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
      return;
    }
    toast.error('Failed to remove appointment from queue');
  } finally {
    loadingModal.close();
  }
};
